import React, { useEffect, useState } from 'react';
import './LoadingAnimations.css';

/**
 * Navigation logo. Byte-for-byte the same file as the Cloudinary image
 * in DEFAULT_LOGO, which the header uses when no custom logo is saved.
 * The reconstructed brand SVG is a different drawing and is not used here.
 */
const NAV_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/carmen-logo.png`;

const GOLD = { r: 226, g: 179, b: 42 };

type VariantId = 'spin' | 'step' | 'pulse';
type StageTheme = 'light' | 'dark';

interface Variant {
  id: VariantId;
  option: string;
  title: string;
  description: string;
  timing: (speed: number) => string;
}

const VARIANTS: Variant[] = [
  {
    id: 'spin',
    option: 'Option A',
    title: 'Continuous rotation',
    description: 'The mark turns through a full circle and repeats without a jump.',
    timing: (speed) => `${(1.8 / speed).toFixed(1)}s per revolution`,
  },
  {
    id: 'step',
    option: 'Option B',
    title: 'Stepped rotation',
    description: 'The mark advances 60 degrees, eases into place, then rests before the next step.',
    timing: (speed) => `${(4.2 / speed).toFixed(1)}s per revolution`,
  },
  {
    id: 'pulse',
    option: 'Option C',
    title: 'Geometric pulse',
    description: 'The mark stays still and breathes with a small change in size and opacity.',
    timing: (speed) => `${(2.8 / speed).toFixed(1)}s per breath`,
  },
];

const prefersReducedMotion = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * The navigation PNG is gold on a solid white plate. This keeps the plate's
 * silhouette and paints the file's own gold so the mark can sit on any stage.
 * The source file is not rewritten.
 */
const useTransparentMark = (src: string) => {
  const [url, setUrl] = useState(src);

  useEffect(() => {
    let cancelled = false;
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return;
      context.drawImage(image, 0, 0);
      const frame = context.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = frame.data;
      const goldBlue = GOLD.b / 255;
      const scale = 1 / (1 - goldBlue);

      for (let index = 0; index < pixels.length; index += 4) {
        const coverage = Math.min(1, Math.max(0, (1 - pixels[index + 2] / 255) * scale));
        pixels[index] = GOLD.r;
        pixels[index + 1] = GOLD.g;
        pixels[index + 2] = GOLD.b;
        pixels[index + 3] = Math.round(coverage * 255);
      }

      context.putImageData(frame, 0, 0);
      if (!cancelled) setUrl(canvas.toDataURL('image/png'));
    };
    image.src = src;
    return () => {
      cancelled = true;
    };
  }, [src]);

  return url;
};

interface MarkProps {
  src: string;
  variant: VariantId;
  runId: number;
  label: string;
  playing: boolean;
}

const AnimatedMark: React.FC<MarkProps> = ({ src, variant, runId, label, playing }) => (
  <div className="loader-stage-frame" role="status" aria-live="polite">
    <span className="sr-only">{playing ? `Loading. ${label}.` : `Loading paused. ${label}.`}</span>
    <div key={runId} className={`cl-loader-rotor cl-loader-rotor--${variant}`}>
      <img src={src} alt="" />
    </div>
  </div>
);

const LoadingAnimations: React.FC = () => {
  const markSrc = useTransparentMark(NAV_LOGO_SRC);
  const [speed, setSpeed] = useState(1);
  const [logoSize, setLogoSize] = useState(104);
  const [theme, setTheme] = useState<StageTheme>('light');
  const [playing, setPlaying] = useState(() => !prefersReducedMotion());
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);
  const [runId, setRunId] = useState(0);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      setReducedMotion(media.matches);
      if (media.matches) setPlaying(false);
    };
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, []);

  return (
    <section
      className={`admin-section loading-animations${playing ? ' is-playing' : ''}`}
      style={{
        ['--cl-speed' as string]: String(speed),
        ['--cl-logo-size' as string]: `${logoSize}px`,
      }}
    >
      <div className="section-header">
        <h2>Loading animation</h2>
        <p>Three experiments with the navigation mark. The public site is unchanged.</p>
      </div>

      <div className="section-content">
        <div className="loader-controls">
          <label className="loader-field">
            <span>Speed <strong>{speed.toFixed(1)}×</strong></span>
            <input
              type="range"
              min={0.5}
              max={2}
              step={0.1}
              value={speed}
              aria-valuemin={0.5}
              aria-valuemax={2}
              aria-valuenow={speed}
              aria-valuetext={`${speed.toFixed(1)} times`}
              onChange={(event) => setSpeed(Math.round(Number(event.target.value) * 10) / 10)}
            />
          </label>

          <label className="loader-field">
            <span>Logo size <strong>{logoSize}px</strong></span>
            <input
              type="range"
              min={64}
              max={144}
              step={4}
              value={logoSize}
              aria-valuemin={64}
              aria-valuemax={144}
              aria-valuenow={logoSize}
              aria-valuetext={`${logoSize} pixels`}
              onChange={(event) => setLogoSize(Number(event.target.value))}
            />
          </label>

          <div className="loader-field">
            <span id="loader-background-label">Background</span>
            <div className="loader-segment" role="group" aria-labelledby="loader-background-label">
              <button
                type="button"
                className={theme === 'light' ? 'is-selected' : ''}
                aria-pressed={theme === 'light'}
                onClick={() => setTheme('light')}
              >
                Light
              </button>
              <button
                type="button"
                className={theme === 'dark' ? 'is-selected' : ''}
                aria-pressed={theme === 'dark'}
                onClick={() => setTheme('dark')}
              >
                Dark
              </button>
            </div>
          </div>

          <div className="loader-actions">
            <button
              type="button"
              className="btn btn-secondary"
              aria-pressed={playing}
              onClick={() => setPlaying((current) => !current)}
            >
              <i className={`fas ${playing ? 'fa-pause' : 'fa-play'}`} aria-hidden="true"></i>
              <span>{playing ? 'Pause' : 'Play'}</span>
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setRunId((current) => current + 1)}
            >
              <i className="fas fa-redo" aria-hidden="true"></i>
              <span>Restart</span>
            </button>
          </div>
        </div>

        {reducedMotion && (
          <p className="loader-motion-note">
            Reduced motion is on, so these previews start paused. Play runs them for this review.
          </p>
        )}

        <div className="loader-grid">
          {VARIANTS.map((variant) => (
            <article className="loader-card" key={variant.id}>
              <p className="option-kicker">{variant.option}</p>
              <h3>{variant.title}</h3>
              <p className="option-copy">{variant.description}</p>
              <div className={`preview-stage is-${theme}`}>
                <AnimatedMark
                  src={theme === 'dark' ? markSrc : NAV_LOGO_SRC}
                  variant={variant.id}
                  runId={runId}
                  label={variant.title}
                  playing={playing}
                />
              </div>
              <p className="option-meta">{variant.timing(speed)}</p>
            </article>
          ))}
        </div>

        <p className="loader-source">
          Uses the navigation logo file. Existing loading indicators are left as they are.
        </p>
      </div>
    </section>
  );
};

export default LoadingAnimations;
